import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { NavigationEnd, Router } from '@angular/router';

import {
  Subscription,
  Subject,
  Observable
} from 'rxjs';
import { filter, skip, takeUntil } from 'rxjs/operators';

import { FormControl } from '@angular/forms';
import { PersonaProviderStore } from '@sondermind/data-access/provider-facing/persona-provider';
import { ClientReferralMemberStore } from '@sondermind/data-access/provider-facing/client-referrals';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { IrisButtonTypes } from '@sondermind/iris/button';
import {
  CombinedClientAndClientReferralMember,
  ClientStatus,
  CLIENT_TYPES
} from '@sondermind/utilities/models-clinical';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';
import { BreakpointsService } from '@sondermind/utilities/services';

import { MatchClientStore } from '../../stores/match-client.store';

interface IClientListTab {
  title: string;
  status: string;
  clientsCount: string;
}

@Component({
  selector: 'member-page-match-clients-list',
  templateUrl: './match-clients-list.component.html',
  styleUrls: ['./match-clients-list.component.scss'],
  standalone: false
})
export class MatchClientsListPage implements OnInit, OnDestroy {
  @ViewChild('tabs', { static: false }) tabs: MatTabGroup;
  notAcceptingClientsModalOpen: boolean = false;
  user: ProviderUser;
  matchClientAnalyticsSub: Subscription;
  clients: CombinedClientAndClientReferralMember[] = [];
  showOlderFormControl = new FormControl<boolean>(false);

  timezone: string;
  matchesState: string = '';
  sortData: { active: string; direction: string; } = { active: 'matched_at', direction: 'desc' };
  matchClientType = CLIENT_TYPES.match;
  clientStatus = ClientStatus;
  matchLimitReached: boolean = false;
  matchLimitTitle: string = `You're at the New Match Limit`;
  matchLimitBody: string = 'To receive more matches, please accept or rematch your current list of "New" matches.';
  showAvailabilityErrorState: number | boolean;

  tabsList: IClientListTab[] = [
    { title: 'New', status: this.clientStatus.NEW_MATCH, clientsCount: '' },
    { title: 'Accepted', status: this.clientStatus.ACCEPTED, clientsCount: '' },
    { title: 'Scheduled', status: this.clientStatus.CLIENT, clientsCount: '' }
  ];

  filterOptions = {
    filter: [
      { key: 'client_type', value: 'potential' }
    ],
    page: {
      size: 15,
      number: 1
    },
    sort: {
      key: 'name',
      asc: 'asc'
    }
  };
  IrisButtonTypes = IrisButtonTypes;

  constructor(
    private appService: SondermindApplicationService,
    private router: Router,
    public matchClientStore: MatchClientStore,
    public breakPoint: BreakpointsService,
    private clientReferralMemberStore: ClientReferralMemberStore,
    private providerStore: PersonaProviderStore,
  ) { }

  private destroyedSubj: Subject<void> = new Subject<void>();
  destroyed$: Observable<void> = this.destroyedSubj.asObservable();

  ngOnInit(): void {
    this.matchClientStore.status = this.clientStatus.NEW_MATCH;
    this.loadAnalytics();
    this.initializeSubs();
  }

  ngOnDestroy(): void {
    this.destroyedSubj.next();
    this.destroyedSubj.complete();
  }

  initializeSubs(): void {
    this.matchClientStore.tableState$.pipe(takeUntil(this.destroyed$)).subscribe((res) => {
      if (res) {
        this.showOlderFormControl.setValue(res[this.clientStatus.ACCEPTED].includeClientsRetainedMoreThan30Days);
      }
    });

    this.appService.currentUser$.pipe(takeUntil(this.destroyed$)).subscribe((u) => {
      this.user = u;
      this.timezone = u.timeZone;
    });

    if (this.user.acceptingClients) {
      this.clientReferralMemberStore.declineReasonId$.pipe(
        takeUntil(this.destroyed$)
      ).subscribe((res) => {
        if (res) {
          this.notAcceptingClientsModalOpen = true;
        }
      });
    }

    this.providerStore.getPersonaProviderProfile$().pipe(takeUntil(this.destroyed$)).subscribe((profile) => {
      this.matchLimitReached = profile.reachedNotifiedLimit;
    });

    this.router.events.pipe(
      takeUntil(this.destroyed$),
      filter((e) => e instanceof NavigationEnd),
    ).subscribe((res: NavigationEnd) => {
      const type = this.router.getCurrentNavigation().extras?.state?.type;
      const isMatches = res.url.includes('matches');
      if (type && isMatches) {
        this.setTabFromRoute(type);
      }
    });
  }

  reload(tab?: Event | { index: number; label: string; }): void {
    if (tab && 'label' in tab) {
      this.matchClientStore.status = this.tabsList.find((key) => tab.label.startsWith(key.title))?.status;
    }
  }

  setTabFromRoute(tab: string): void {
    const tableState = {
      page: 1,
      pageIndex: 0,
      pageSize: 15,
      sortBy: 'matched_at',
      sortAscending: false,
      sortDirection: 'desc',
      includeClientsRetainedMoreThan30Days: true
    };

    this.matchClientStore.selectedTab = tab === 'accepted' ? 1 : 0;
    this.matchClientStore.status = tab === 'accepted' ? 'accepted' : 'new_match';
    tableState.includeClientsRetainedMoreThan30Days = tab !== 'accepted';
    this.matchClientStore.updateTableState(tableState);
  }

  closeNotAcceptingClientsModal(): void {
    this.notAcceptingClientsModalOpen = false;
    this.clientReferralMemberStore.declineReasonIdSubj.next(false);
  }

  private loadAnalytics(): void {
    this.matchClientStore.loadMatchClientAnalytics();
    this.matchClientAnalyticsSub = this.matchClientStore.matchClientAnalytics$
      .pipe(
        takeUntil(this.destroyed$),
        skip(1),
      ).subscribe((ana̵lytics) => {
        this.tabsList.forEach((tab, index, tabsList) => {
          if (tab.status === ClientStatus.NEW_MATCH) {
            /* eslint-disable no-param-reassign */
            tabsList[index].clientsCount = ana̵lytics.newMatchCountLabel;
          } else if (tab.status === ClientStatus.ACCEPTED) {
            tabsList[index].clientsCount = ana̵lytics.acceptedCountLabel;
          } else if (tab.status === ClientStatus.CLIENT) {
            tabsList[index].clientsCount = ana̵lytics.scheduledCountLabel;
            /* eslint-enable no-param-reassign */
          }
        });
        this.showAvailabilityErrorState = ana̵lytics.newMatch === 0 && (ana̵lytics.accepted > 0 || ana̵lytics.scheduled > 0);
        this.matchesState = this.tabsList.some((tab) => tab.clientsCount) ? 'matches' : 'noMatches';
      });
  }
}
